import AppContext from 'App/AppContext';
import { IPDefaultButton, IPSpinner } from 'baseComponents';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import DialogYesNo from 'components/Dialogs/DialogYesNo';
import FormStructureConflictModal from 'components/Tasks/Forms/FormStructureConflictModal';
import TaskTypeFormEditor from 'components/Tasks/Forms/TaskTypeFormEditor';
import {
  buildFormDiscardCommand,
  prepareDesignTimeFormElements,
} from 'components/Tasks/Forms/taskTypeFormEditor.helpers';
import { useFormStructureAutosave } from 'components/Tasks/Forms/useFormStructureAutosave';
import KPI from 'models/kpi/kpi';
import TaskType from 'models/tasks/taskType';
import TaskTypeFormElement from 'models/tasks/taskTypeFormElement';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetKPIsAndFields } from 'services/Api/kpiService';
import { apiGetTaskTypeForm } from 'services/Api/taskService';
import { apiRequest } from 'services/Auth/authConfig';

interface IEmbeddedTaskTypeFormEditorProps {
  // The form to edit. For a generated (hidden) form this is the stand-in TaskType.forTask returns:
  // it carries the real id, which is all the read and the save need - the server keeps the name and
  // the translations of a generated form to itself.
  taskType: TaskType;
  editable: boolean;
  compact?: boolean;
  // The names of the entities whose criteria are projected onto this form. With them the editor
  // shows the questions entity by entity and offers the criteria commands.
  entityNamesByKey?: Map<string, string>;
  // Raised with the accepted form whenever a change forks the family into a new version: the task
  // hosting this tab is pinned to that form, and the server has moved that pin along.
  onFormVersionChanged?: (taskType: TaskType) => void;
}

// The form editor as a TAB rather than a modal: the form IS the procedure, so there is nothing to
// gain from asking the user to open a second window to change it. Every change persists on its own
// (there is no Save of its own here - the surrounding modal's Save is the TASK's), which is the same
// contract the forms administration uses.
export const EmbeddedTaskTypeFormEditor = (props: IEmbeddedTaskTypeFormEditorProps) => {
  const { t } = useTranslation(['forms', 'translation']);
  const appContext = useContext(AppContext);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [formElements, setFormElements] = useState<TaskTypeFormElement[]>([]);
  const [kpis, setKPIs] = useState<KPI[]>([]);
  const [currentTaskType, setCurrentTaskType] = useState<TaskType>(props.taskType);
  const [showDiscardChanges, setShowDiscardChanges] = useState<boolean>(false);
  const [showStructureConflict, setShowStructureConflict] = useState<boolean>(false);

  // A change to a form that is already in use does not amend it: it forks the family into a new
  // version with an id of its own. Every accepted form therefore lands here, because this tab is not
  // the only one holding it - the catalogue every other surface resolves a form through has to follow
  // the family, and the task this tab belongs to has been repointed server-side, so without telling it
  // its next form read asks for a version it is no longer pinned to.
  const adoptTaskType = (taskType: TaskType) => {
    appContext.globalDataCache.taskTypes.update(taskType);
    setCurrentTaskType(taskType);
    props.onFormVersionChanged?.(taskType);
  };

  const formAutosave = useFormStructureAutosave({
    taskType: currentTaskType,
    // The accepted elements ARE the new form, so they are applied rather than read back: a refetch
    // here blanked the tab for a spinner a second after every reorder.
    onAccepted: (result, elements) => {
      adoptTaskType(result.taskType);
      setFormElements(prepareDesignTimeFormElements(elements));
    },
    onError: (error) => appContext.setError(error),
  });

  useEffect(() => {
    if (formAutosave.failure === 'conflict') {
      setShowStructureConflict(true);
    }
  }, [formAutosave.failure]);

  useEffect(() => {
    setCurrentTaskType(props.taskType);
    void loadData(props.taskType.taskTypeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.taskType.taskTypeId]);

  const loadData = async (taskTypeId = currentTaskType.taskTypeId) => {
    try {
      setIsLoading(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const elements = await apiGetTaskTypeForm(taskTypeId, accessToken);

      setFormElements(prepareDesignTimeFormElements(elements));

      const allKpis = await apiGetKPIsAndFields(accessToken, appContext.globalDataCache);
      setKPIs(allKpis);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const persist = (newElements: TaskTypeFormElement[]) => {
    setFormElements(newElements);
    formAutosave.enqueue(newElements);
  };

  // An archived form is read-only wherever it can be opened: its structure is frozen and its preview
  // must not invite answers, exactly as the forms administration shows it.
  const editable = props.editable && !currentTaskType.isArchived;

  // The discard response is the raw server projection WITHOUT the KPI graph, so applying it leaves
  // every KPI question rendering empty (SingleFormElement needs element.kpi). Read the form back to
  // restore the full elements; the head keeps its id on a discard, it only rewinds its content.
  const discardSessionChanges = async () => {
    setShowDiscardChanges(false);
    const discarded = await formAutosave.discardSession();
    if (discarded) {
      await loadData(formAutosave.getCurrentTaskType()?.taskTypeId ?? currentTaskType.taskTypeId);
    }
  };

  //
  // Main render
  //
  if (isLoading) {
    return (
      <IPStack horizontalAlign="center" styles={{ root: { paddingTop: 40 } }}>
        <IPSpinner size={IPSpinnerSize.medium} />
      </IPStack>
    );
  }

  return (
    <>
      {/* Recovery only: Discard is a command on the form and lives on the form's own toolbar. */}
      {editable && formAutosave.failure && (
        <IPStack horizontal horizontalAlign="end" tokens={{ childrenGap: 8 }} styles={{ root: { paddingBottom: 8 } }}>
          {formAutosave.failure === 'transport' && (
            <IPDefaultButton
              iconProps={{ iconName: 'Refresh' }}
              text={t('forms:Versioning.Autosave.Retry')}
              onClick={() => void formAutosave.retry()}
            />
          )}
          {formAutosave.failure === 'conflict' && (
            <IPDefaultButton
              iconProps={{ iconName: 'Compare' }}
              text={t('forms:Versioning.Conflict.Resolve')}
              onClick={() => setShowStructureConflict(true)}
            />
          )}
        </IPStack>
      )}
      <TaskTypeFormEditor
        taskTypeId={currentTaskType.taskTypeId}
        formTaskType={currentTaskType}
        getCurrentFormTaskType={formAutosave.getCurrentTaskType}
        kpis={kpis}
        onKPIsChange={setKPIs}
        formElements={formElements}
        editable={editable}
        readonly={!editable}
        isSystemForm={!!currentTaskType.systemTaskType}
        isLoading={isSaving}
        compact={props.compact ?? true}
        hideEmptyFormText={true}
        designTime={true}
        showFieldCards={true}
        // The add/edit field surfaces must be modals here, not side panels: a panel renders into the
        // app's right-side layer host (z-index 800), which sits BEHIND the task modal this editor
        // lives in - the panel opens but is never visible. The form modal used the same setting.
        fieldPanelAsPanel={false}
        formFieldModalAsPanel={false}
        confirmFieldRemoval={true}
        toolbarCommands={[buildFormDiscardCommand(t, formAutosave.canDiscard, () => setShowDiscardChanges(true))]}
        onElementsChange={(next, { persist: shouldPersist }) =>
          shouldPersist ? void persist(next) : setFormElements(next)
        }
        onSavingChange={setIsSaving}
        onStructureCommitted={(taskType, elements) => {
          formAutosave.rebase(taskType);
          adoptTaskType(taskType);
          setFormElements(elements);
          void loadData(taskType.taskTypeId);
        }}
        onBeforeDefinitionCommit={formAutosave.flush}
        onDefinitionCommitted={(result) => {
          const current = result.updatedTaskTypes.find(
            (taskType) => taskType.taskTypeFamilyId === currentTaskType.taskTypeFamilyId,
          );
          if (current) {
            formAutosave.rebase(current);
            adoptTaskType(current);
            void loadData(current.taskTypeId);
          }
        }}
        entityNamesByKey={props.entityNamesByKey}
        // criteria writes re-project the form server-side; re-reading is what makes the
        // fork/retire/new order visible here
        onCriteriaChanged={() => void loadData(currentTaskType.taskTypeId)}
      />
      {showDiscardChanges && (
        <DialogYesNo
          hidden={!showDiscardChanges}
          title={t('forms:Versioning.Autosave.DiscardTitle')}
          subText={t('forms:Versioning.Autosave.DiscardText')}
          yesText={t('translation:General.Button.Discard')}
          noText={t('translation:General.Button.Cancel')}
          onNo={() => setShowDiscardChanges(false)}
          onYes={() => void discardSessionChanges()}
        />
      )}
      {showStructureConflict && currentTaskType.taskTypeFamilyId && (
        <FormStructureConflictModal
          taskTypeFamilyId={currentTaskType.taskTypeFamilyId}
          localFormElements={formElements}
          isOpen={showStructureConflict}
          onDismiss={() => setShowStructureConflict(false)}
          onReload={(taskType, elements) => {
            formAutosave.rebase(taskType);
            adoptTaskType(taskType);
            setFormElements(elements);
          }}
          onReapply={(taskType, elements) => formAutosave.reapply(taskType, elements)}
        />
      )}
    </>
  );
};

export default EmbeddedTaskTypeFormEditor;
